import { useEffect, useState } from "react";
import {
  InvestigationEvent,
  InvestigationLink,
  Stage,
} from "../../lusca-search-api/generated-src";
import { InvestigationsApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import { commentText } from "./InvestigationTimeline";

/** CR, task/ticket and MCM URLs, with the id shown as their label (mirrors the backend's link_label). */
const LINK_ID_PATTERNS: RegExp[] = [
  /^https?:\/\/code\.amazon\.com\/reviews\/(CR-\d+)/i,
  /^https?:\/\/taskei\.amazon\.dev\/tasks\/([A-Za-z][A-Za-z0-9]*-\d+)/,
  /^https?:\/\/(?:issues|sim)\.amazon\.com\/issues\/([A-Za-z][A-Za-z0-9]*-\d+)/,
  /^https?:\/\/t\.corp\.amazon\.com\/([A-Za-z]?\d{6,})\b/,
  /^https?:\/\/mcm\.amazon\.dev\/cms\/(MCM-\d+)/i,
];

function linkId(url: string): string | null {
  const match = LINK_ID_PATTERNS.map((p) => p.exec(url)).find(Boolean);
  if (!match) return null;
  const id = match[1];
  return /^(cr|mcm)-/i.test(id) ? id.toUpperCase() : id;
}

/** What a link shows: the label a person typed, else the id in its URL (CR-123…), else the URL. */
export function linkDisplayLabel(entry: InvestigationLink): string {
  const label = (entry.label || "").trim();
  if (label && label !== entry.url) return label;
  return linkId(entry.url) || entry.url;
}

/** Same URL for de-duplication: no trailing slash, CR revision/anchor dropped. */
function urlKey(url: string): string {
  const cr = /^https?:\/\/code\.amazon\.com\/reviews\/(CR-\d+)/i.exec(url);
  if (cr) return `cr:${cr[1].toUpperCase()}`;
  return url.replace(/[/#]+$/, "").toLowerCase();
}

const URL_RE = /https?:\/\/[^\s<>()[\]"'`]+/g;
// A bare "CR-123456789" in prose (not part of a URL).
const BARE_CR_RE = /(^|[^\w/-])(CR-\d{6,})\b/gi;

/** CR, ticket and MCM links written in a piece of comment text, in order, without duplicates. */
export function evidenceLinksIn(text: string): string[] {
  const matches: string[] = text.match(URL_RE) || [];
  const urls = matches
    .map((match) => match.replace(/[.,;:!?*_]+$/, ""))
    .filter((url) => linkId(url));
  const bare: string[] = Array.from(
    text.matchAll(BARE_CR_RE),
    (match: RegExpMatchArray) =>
      `https://code.amazon.com/reviews/${String(match[2]).toUpperCase()}`
  );
  const seen = new Set<string>();
  return [...urls, ...bare].filter((url) => {
    const key = urlKey(url);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export interface CommentLink {
  url: string;
  label: string;
  /** Who wrote the (oldest) comment the link came from. */
  author?: string;
}

/**
 * Evidence links people wrote in comments that are not evidence links of the case yet, oldest
 * first. `events` may hold any timeline events; only live comments are read.
 */
export function commentLinksToAdd(
  events: InvestigationEvent[],
  existing: InvestigationLink[]
): CommentLink[] {
  const known = new Set(existing.map((l) => urlKey(l.url)));
  return events
    .filter((e) => e.type === "COMMENT" && !e.deletedAt)
    .sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""))
    .flatMap((event) =>
      evidenceLinksIn(commentText(event)).map((url) => ({
        url,
        label: linkId(url) || url,
        author: event.actor || undefined,
      }))
    )
    .filter((link) => {
      const key = urlKey(link.url);
      if (known.has(key)) return false;
      known.add(key);
      return true;
    });
}

/** Timeline pages read when looking for comment links (50 events each). */
const MAX_TIMELINE_PAGES = 10;

/**
 * Every comment link not yet in the case's evidence, read once when the Resolve dialog opens.
 * A failed read just offers nothing: the links can still be added on the Conclusion tab.
 */
export function useCommentLinks(
  stage: Stage | undefined,
  investigationId: string,
  existing: InvestigationLink[],
  enabled: boolean
) {
  const [events, setEvents] = useState<InvestigationEvent[] | null>(null);
  const active = enabled && !!stage;
  useEffect(() => {
    if (!active || !stage) return undefined;
    let cancelled = false;
    const api = InvestigationsApiFactory();
    const load = async (
      token: string | undefined,
      pagesLeft: number
    ): Promise<InvestigationEvent[]> => {
      const { data } = await api.getInvestigationTimeline(
        stage,
        investigationId,
        50,
        token
      );
      const page = data.events || [];
      if (!data.nextToken || pagesLeft <= 1) return page;
      return [...page, ...(await load(data.nextToken, pagesLeft - 1))];
    };
    load(undefined, MAX_TIMELINE_PAGES)
      .then((read) => !cancelled && setEvents(read))
      .catch(() => !cancelled && setEvents([]));
    return () => {
      cancelled = true;
    };
  }, [active, stage, investigationId]);
  return {
    links: events ? commentLinksToAdd(events, existing) : [],
    loading: active && events === null,
  };
}
